/* @jsx h */
import { defineComponent, h, Fragment, ref } from "vue";
import { RouterLink, useRoute } from "vue-router";
import { isPackaged } from "@lemmonhq/orders";
import { routingFetch } from "@app/data/helpers";
import { useLocalization } from "@lemmonhq/uikit";

const getBackofficeLink = (section: string) => {
  return `/api/redirect/backoffice?to=${section}`;
};

export const navItemClass = (isActive: boolean) => {
  return `text-1xl inline-flex w-full items-center gap-0  lg:w-auto lg:pl-1 border-l-4 lg:border-l-0 relative px-1 py-2 ${
    isActive
      ? "border-l-primary lg:border-l-transparent text-accent"
      : "border-transparent"
  }`;
};

export const navItemContentClass =
  "flex min-w-0 flex-1 items-center gap-2 lg:gap-0.5";
export const navItemIconClass =
  "material-symbols-outlined w-6 shrink-0 text-center text-2xl leading-none";
export const navItemTextClass = "min-w-0 text-1xl lg:text-sm";
export const navExternalIconClass =
  "material-symbols-outlined ml-auto shrink-0 text-base leading-none opacity-70";

export const MagicLink = defineComponent<{
  to: string;
  icon: string;
  title: string;
}>({
  name: "MagicLink",
  props: ["to", "icon", "title"],
  setup(props) {
    const loading = ref(false);
    const backOfficeLink = getBackofficeLink(props.to);
    const handleClick = async (e: MouseEvent) => {
      if (true) {
        e.preventDefault();
        try {
          loading.value = true;
          const response = await routingFetch(backOfficeLink, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          });
          if (!response.ok) {
            throw new Error("Failed to fetch backoffice link");
          }
          const data: any = await response.json();
          window.open(data.url, "_blank");
        } catch (e) {
          alert("Failed to log you into backoffice");
        } finally {
          loading.value = false;
        }
      } else {
        // do nothing
      }
    };
    const { translations } = useLocalization();
    return () => {
      return (
        <a
          class={linkClass(false)}
          onClick={handleClick}
          href={backOfficeLink}
          target="_blank"
        >
          <>
            {loading.value ? (
              <span class="w-6 shrink-0 flex justify-center items-center">
                <span
                  style="width: 18px;"
                  class="loading loading-spinner loading-xs"
                />
              </span>
            ) : (
              <span class={navItemIconClass}>{props.icon}</span>
            )}{" "}
            <span class={navItemTextClass}>
              {translations.value[props.title] || props.title}
            </span>
            <span class={navExternalIconClass}>open_in_new</span>
          </>
        </a>
      );
    };
  },
});
export const linkClass = (isActive: boolean) => {
  return navItemClass(isActive);
};
export type NavLinkProps = {
  to: string;
  icon: string;
  title: string;
  il8n?: string;
  external?: boolean;
  onClick?: Function;
  parents?: true[];
  comparatorFn?: (routePath: string, path: string) => boolean;
  badge?: number;
};

export const NavLink = defineComponent<NavLinkProps>({
  name: "NavLink",
  props: [
    "to",
    "icon",
    "title",
    "external",
    "onClick",
    "comparatorFn",
    "parents",
    "il8n",
    "badge",
  ],
  setup(props) {
    const route = useRoute();
    const comparatorFn = props.comparatorFn
      ? props.comparatorFn
      : (routePath: string, path: string) => routePath === path;
    const isActive = () => comparatorFn(route.path, props.to);
    const { t, translations } = useLocalization();
    const content = () => (
      <Fragment>
        <span
          class={navItemContentClass}
          style={`padding-left: ${props.parents ? props.parents.length * 15 : "0"}px; `}
        >
          <span class={navItemIconClass}>{props.icon}</span>{" "}
          <span class={navItemTextClass}>
            {translations.value[props.title] || props.title}
          </span>
          {props.badge && props.badge > 0 ? (
            <span
              class="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 text-xs font-semibold rounded-md bg-error text-error-content"
              data-testid={`nav-badge-${props.to.replace(/\//g, "-").replace(/^-/, "")}`}
            >
              {props.badge}
            </span>
          ) : null}
        </span>
      </Fragment>
    );

    return () => {
      if (props.external) {
        return (
          <MagicLink to={props.to} title={props.title} icon={props.icon}>
            {content()}
          </MagicLink>
        );
      }
      // todo: instead of this onClick which could be depreceated, use a custom RouterLink component
      return (
        <RouterLink
          {...{
            onClick: (e: MouseEvent) => {
              props.onClick && props.onClick(e);
            },
          }}
          to={props.to}
          class={linkClass(isActive())}
        >
          {() => content()}
        </RouterLink>
      );
    };
  },
});
